import { useEffect, useState } from 'react';
import { NetworkQualityIndicator } from './NetworkQualityIndicator';
import { MicIcon, MicOffIcon, SwapIcon } from './icons';

// Geometry of the inset self-view. Shared by the two media elements and by the
// chrome layer that sits on top of whichever one is currently inset, so the
// frame, its label and the swap control stay glued together at both breakpoints.
const PIP_FRAME = 'absolute bottom-10 right-10 w-36 h-48 lg:w-52 lg:h-64';
const PIP_MEDIA =
  `${PIP_FRAME} z-20 object-cover bg-neutral-800 shadow-2xl border-2 border-brand-gold/30`;
const STAGE_MEDIA = 'absolute inset-0 w-full h-full object-cover';

/**
 * Your own face belongs in a mirror. Everyone expects a self-view to move the way
 * a bathroom mirror does; an unmirrored one reads as subtly wrong even to people
 * who cannot say why. The seller's app has always done this
 * (`twilio-video-player2/participant.component.ts`) — this side simply never did,
 * so the two apps disagreed once the swap made both feeds large.
 *
 * The one camera that must NOT be flipped is the rear one: there the picture is
 * of the world, not of you, and mirroring it renders text backwards. Same rule as
 * the seller's app — mirror unless the track says `facingMode: 'environment'`.
 * This matters more here than there: this app is opened from a link, often on a
 * phone, and the customer can switch cameras mid-call.
 */
function useSelfViewMirrored(ref: React.RefObject<HTMLVideoElement | null>, isCameraOn: boolean): boolean {
  const [mirrored, setMirrored] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const read = () => {
      const stream = el.srcObject as MediaStream | null;
      const facing = stream?.getVideoTracks()[0]?.getSettings().facingMode;
      setMirrored(facing !== 'environment');
    };

    read();
    // Re-read whenever a different stream is attached — switching camera mid-call
    // replaces the track without anything else in this component changing.
    el.addEventListener('loadedmetadata', read);
    return () => el.removeEventListener('loadedmetadata', read);
  }, [ref, isCameraOn]);

  return mirrored;
}

interface VideoPanelProps {
  remoteVideoRef: React.Ref<HTMLVideoElement>;
  remoteAudioRef?: React.Ref<HTMLAudioElement>;
  localVideoRef: React.RefObject<HTMLVideoElement | null>;
  advisorName?: string;
  advisorTitle?: string;
  /** Organization logo URL — shown in the waiting-for-seller circle instead of an initial */
  organizationImage?: string;
  isCameraOn: boolean;
  showPiP?: boolean;
  /** Local participant network quality level (0-5) */
  networkQualityLevel?: number;
  /** Whether the remote seller participant has joined the video room */
  sellerConnected: boolean;
  /** Minutes to wait before showing a delay message (default: 3) */
  sellerDelayMinutes?: number;
  /** Customer's own microphone state — drives the self-mute toggle UI. */
  isMicOn?: boolean;
  /** Toggle customer's own microphone. When absent, the mute button is not rendered. */
  onToggleMic?: () => void;
}

export function VideoPanel({
  remoteVideoRef,
  remoteAudioRef,
  localVideoRef,
  advisorName,
  advisorTitle,
  organizationImage,
  isCameraOn,
  showPiP = true,
  networkQualityLevel = 0,
  sellerConnected,
  sellerDelayMinutes = 3,
  isMicOn,
  onToggleMic,
}: VideoPanelProps) {
  const [isDelayed, setIsDelayed] = useState(false);
  // W7: which feed occupies the main stage. Session-local by design — the layout
  // choice is a moment-to-moment preference, not a setting.
  const [isSelfMain, setIsSelfMain] = useState(false);

  // Swapping only means anything when there are two live feeds to exchange.
  const canSwap = showPiP && sellerConnected && isCameraOn;
  const selfIsMain = canSwap && isSelfMain;
  // Follows the local feed into whichever frame it occupies — the mirror is a
  // property of whose face it is, not of how big the box is.
  const selfMirrored = useSelfViewMirrored(localVideoRef, isCameraOn);

  // Start a timeout when seller is not connected; show delay message after threshold.
  // Clear the timer when seller joins or when component unmounts.
  useEffect(() => {
    if (sellerConnected) {
      setIsDelayed(false);
      return;
    }

    const ms = sellerDelayMinutes * 60 * 1000;
    const timer = setTimeout(() => setIsDelayed(true), ms);
    return () => clearTimeout(timer);
  }, [sellerConnected, sellerDelayMinutes]);

  return (
    <div className="relative w-full h-full max-h-[70vh] lg:max-h-full bg-neutral-900 overflow-hidden shadow-2xl">
      {/* Remote video (advisor) — hidden while seller has not yet joined.
          muted is intentional: remote audio is attached to a separate <audio> element
          below, so muting the <video> avoids Chrome's autoplay block on unmuted media
          when user activation has expired (buyer-first waiting scenario).

          The swap moves this element between the stage and the inset frame by
          class only. Both <video> elements keep their position in the tree at all
          times: useLiveVideo re-attaches the LOCAL track solely when the track
          object changes, so a remount here would leave the self-view permanently
          black. Restyling is the whole mechanism — nothing detaches. */}
      <video
        ref={remoteVideoRef}
        data-testid="remote-video"
        autoPlay
        playsInline
        muted
        className={`${selfIsMain ? PIP_MEDIA : STAGE_MEDIA} transition-opacity duration-700 ${sellerConnected ? 'opacity-100' : 'opacity-0'}`}
      />

      {/* Local self-view. Sits next to the remote feed rather than in a frame of its
          own so the two can trade geometry without either leaving the tree; both stay
          beneath the gradient / plaque / placeholder that follow, and the inset one
          lifts itself above them via z-20 in PIP_MEDIA.
          Any mirror transform belongs on this element, so it follows the feed into
          whichever frame it currently occupies. */}
      {showPiP && (
        isCameraOn ? (
          <video
            ref={localVideoRef}
            data-testid="local-video"
            autoPlay
            playsInline
            muted
            className={`${selfIsMain ? STAGE_MEDIA : PIP_MEDIA}${selfMirrored ? ' -scale-x-100' : ''}`}
          />
        ) : (
          <div className={`${PIP_MEDIA} flex items-center justify-center bg-gray-800`}>
            <span className="text-white text-xs">Camera Off</span>
          </div>
        )
      )}

      {/* Remote audio sink — hidden, carries advisor's microphone stream */}
      {remoteAudioRef && (
        <audio ref={remoteAudioRef} autoPlay className="hidden" />
      )}

      {/* Seller-absent placeholder — shown until seller video is available */}
      {!sellerConnected && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-neutral-900">
          {/* Organization logo circle (fallback: empty gold circle).
              object-contain so non-square brand logos render in full (with
              letterboxing inside the circle) instead of being cropped. */}
          <div className="w-24 h-24 rounded-full border-2 border-brand-gold/60 bg-brand-gold/10 overflow-hidden flex items-center justify-center">
            {organizationImage && (
              <img
                src={organizationImage}
                alt=""
                className="w-full h-full object-contain"
              />
            )}
          </div>

          {/* Status text */}
          <p
            className="text-center text-white/80 font-serif font-light text-xl leading-relaxed max-w-xs px-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {isDelayed
              ? 'Your specialist seems to be delayed. You can browse products or chat while waiting.'
              : 'Your specialist will join shortly'}
          </p>

        </div>
      )}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/40 via-transparent to-transparent" />

      {/* Plaque for whoever is on the main stage. After a swap that is the customer,
          so naming the advisor here would caption the wrong face. */}
      {sellerConnected && (
        <div className="absolute bottom-8 left-8 text-white space-y-1">
          {selfIsMain ? (
            <p className="text-xs tracking-[0.2em] uppercase opacity-70 font-sans">
              You
            </p>
          ) : (
            <>
              <p className="text-xs tracking-[0.2em] uppercase opacity-70 font-sans">
                Your Specialist
              </p>
              <p className="text-2xl font-serif font-light">
                {advisorName}
              </p>
              {advisorTitle && (
                <p className="text-sm opacity-80 font-sans">
                  {advisorTitle}
                </p>
              )}
            </>
          )}
          {networkQualityLevel > 0 && (
            <div className="pt-2">
              <NetworkQualityIndicator level={networkQualityLevel} />
            </div>
          )}
        </div>
      )}

      {/* Chrome for the inset frame: label, gradient and the swap control. Stays
          put while the two feeds trade places underneath it. */}
      {showPiP && (
        <div className={`${PIP_FRAME} z-30 pointer-events-none`}>
          <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/40 via-transparent to-transparent" />

          <div className="absolute bottom-3 left-3 text-white text-xs tracking-wide font-sans">
            {selfIsMain ? (advisorName ?? 'Your Specialist') : 'You'}
          </div>

          {canSwap && (
            <button
              type="button"
              onClick={() => setIsSelfMain((prev) => !prev)}
              data-testid="swap-layout"
              className="pointer-events-auto absolute top-2 right-2 w-8 h-8 rounded-full bg-brand-navy/70 hover:bg-brand-navy border border-brand-gold/40 text-white backdrop-blur-sm flex items-center justify-center transition-colors"
              aria-label="Swap the large and small video"
              aria-pressed={selfIsMain}
            >
              <SwapIcon size={14} strokeWidth={1.75} />
            </button>
          )}
        </div>
      )}

      {/* Self-mic toggle — bottom-center, standard video-call UX placement.
          Bottom-left is reserved for the specialist plaque (shown when seller
          connects), bottom-right for the PiP self-view; centering avoids both.
          Hidden entirely when onToggleMic is not wired (chat-only flows). */}
      {onToggleMic && (
        <button
          onClick={onToggleMic}
          className={`absolute bottom-10 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full backdrop-blur-sm flex items-center justify-center transition-colors shadow-lg border-2 ${
            isMicOn
              ? 'bg-brand-navy/60 hover:bg-brand-navy/80 border-brand-gold/40 text-white'
              : 'bg-red-600/80 hover:bg-red-600 border-red-400/60 text-white'
          }`}
          aria-label={isMicOn ? 'Mute microphone' : 'Unmute microphone'}
          aria-pressed={!isMicOn}
        >
          {isMicOn ? <MicIcon size={20} strokeWidth={1.75} /> : <MicOffIcon size={20} strokeWidth={1.75} />}
        </button>
      )}
    </div>
  );
}
